import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Empty, Spin, message } from 'antd';

import { triggerDownloadFromUrl } from '../../utils/download';

import './preview-panel.css';

const PreviewPanel = ({ isLoading, generation, errorMessage }) => {
  const hasImage = Boolean(generation && generation.image && generation.image.url);
  const isCharacterSwap = generation && generation.taskType === 'character_swap';
  const isSensitiveContentError = /sensitive content|public person|brand|protected likeness/i.test(errorMessage || '');
  const promptText = (generation && generation.prompt) || 'Generated artwork';
  const imageUrl = hasImage ? generation.image.url : null;
  const downloadFilename =
    (generation && generation.image && generation.image.filename) || 'generated-image.png';

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadLatest = async () => {
    if (!imageUrl || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      await triggerDownloadFromUrl(imageUrl, downloadFilename);
    } catch (error) {
      message.error('Unable to download image. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section className="preview-panel">
      <div className="preview-header">
        <h3>Result</h3>
      </div>
      <div className="preview-body">
        {isLoading ? (
          <div className="preview-loading" aria-live="polite">
            <Spin size="large" />
            <p>Generating your image...</p>
          </div>
        ) : errorMessage && !hasImage ? (
          <div className="preview-error-state" role="alert">
            <div className="preview-error-state__icon" aria-hidden="true">
              <i className="fa-solid fa-circle-exclamation" />
            </div>
            <div className="preview-error-state__content">
              <h4>{isSensitiveContentError ? 'Request blocked by safety filter' : 'Generation not completed'}</h4>
              <p>{errorMessage}</p>
              <span>
                {isSensitiveContentError
                  ? 'Adjust the text or reference image, or try another supported model.'
                  : 'Adjust your prompt, model, or reference inputs and try again.'}
              </span>
            </div>
          </div>
        ) : hasImage ? (
          <figure className="preview-result">
            <img src={generation.image.url} alt={promptText} />
            {imageUrl && (
              <div className="preview-actions">
                <Button
                  icon={<i className="fa-solid fa-download" aria-hidden="true" />}
                  onClick={handleDownloadLatest}
                  loading={isDownloading}
                  disabled={!imageUrl || isDownloading}
                >
                  Download image
                </Button>
              </div>
            )}
            {!isCharacterSwap && (
              <figcaption>
                <h4>Prompt</h4>
                <p>{promptText}</p>
              </figcaption>
            )}
          </figure>
        ) : (
          <div className="preview-placeholder">
            <Empty description="Your generated images will appear here" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}

        {errorMessage && !isLoading && hasImage && (
          <div className="preview-error" role="alert">
            <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
            <span>{errorMessage}</span>
          </div>
        )}
      </div>
    </section>
  );
};

PreviewPanel.propTypes = {
  isLoading: PropTypes.bool,
  generation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    prompt: PropTypes.string,
    taskType: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string,
      key: PropTypes.string,
      filename: PropTypes.string,
    }),
  }),
  errorMessage: PropTypes.string,
};

PreviewPanel.defaultProps = {
  isLoading: false,
  generation: null,
  errorMessage: null,
};

export default PreviewPanel;
